import { useCallback, useEffect } from 'react'

import { t } from 'i18next'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { CardEtapaContent, CardEtapaItem } from '@/features/proposta/components/ui'
import { DistribuidorPercentual } from '@/features/proposta/entities/ParticipacaoComprador/distribuicao-participacao'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import CardInfoPercentual from '@/pages/proposta/etapa-detalhes-da-proposta/card-info-percentual'
import { Render } from '@/shared/components/render-if'
import { NumberInputField } from '@/shared/components/ui'
import { ComboboxField } from '@/shared/components/ui/combobox'
import { Cnpj } from '@/shared/utils/cnpj'
import { Cpf } from '@/shared/utils/cpf'

const TOTAL_PERCENTUAIS_EM_CENTESIMOS = 10_000

type TParticipacaoCompradorContentProps = {
  compradores: TCompradoresCadastrados
}
export const ParticipacaoCompradorContent = ({ compradores }: TParticipacaoCompradorContentProps) => {
  const {
    control,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext<TPropostaSchemaInput>()

  const cadastroCompradores = compradores
    .map((comprador) => {
      return {
        ...comprador,
        label: `${comprador.nome} - ${comprador.tipo === 'PessoaFisica' ? new Cpf(comprador.documento).format() : new Cnpj(comprador.documento).format()}`,
      }
    })
    .sort((a, b) => a.nome.localeCompare(b.nome))

  const titularId = watch('detalhesDaProposta.participacaoCompradores.titular.id')
  const titularPercentual = watch('detalhesDaProposta.participacaoCompradores.titular.percentualParticipacao')
  const compradoresNaoTitular = watch('detalhesDaProposta.participacaoCompradores.compradores')

  const compradoresFiltrados = useCallback(() => {
    const titularIdAtualizado = getValues('detalhesDaProposta.participacaoCompradores.titular.id')
    return cadastroCompradores?.filter((c) => c.id !== titularIdAtualizado)
  }, [cadastroCompradores, getValues])

  const { fields, replace } = useFieldArray({
    control,
    name: 'detalhesDaProposta.participacaoCompradores.compradores',
    keyName: 'idField',
  })

  const handleLimparParticipacoesQuandoAlteradoCompradores = () => {
    const titularIdAtualizado = getValues('detalhesDaProposta.participacaoCompradores.titular.id')
    const compradoresIds = new Set(cadastroCompradores.map((c) => c.id))
    const preenchidosIds = new Set([titularIdAtualizado, ...compradoresNaoTitular.map((c) => c.id)])

    const listasSaoIguais =
      compradoresIds.size === preenchidosIds.size && [...compradoresIds].every((id) => preenchidosIds.has(id))

    if (!listasSaoIguais) {
      setValue('detalhesDaProposta.participacaoCompradores.titular', {
        id: '',
        percentualParticipacao: 0,
      })
      replace([])
    }
  }

  function gerarDistribuicaoParticipacao(titularPct: number, compradorSimplificado: TCompradoresCadastrados) {
    const percentualTitularEmCentesimos = Math.round(titularPct * 100)
    const percentualRestanteEmCentesimos = TOTAL_PERCENTUAIS_EM_CENTESIMOS - percentualTitularEmCentesimos
    const percentualRestante = percentualRestanteEmCentesimos / 100
    return new DistribuidorPercentual(percentualRestante).distribuir(compradorSimplificado.map((c) => c.id))
  }

  const handleDistribuirParticipacoesQuandoPercentualTitularAlterado = () => {
    const formBase = 'detalhesDaProposta.participacaoCompradores'
    const percentualTitularAtualizado = getValues(`${formBase}.titular.percentualParticipacao`) ?? 0
    const compradoresRestantes = compradoresFiltrados()
    const compradoresComPercentualDistribuido = gerarDistribuicaoParticipacao(
      percentualTitularAtualizado,
      compradoresRestantes,
    )
    replace(compradoresComPercentualDistribuido)
    trigger(`${formBase}.compradores`)
  }

  useEffect(() => {
    handleLimparParticipacoesQuandoAlteradoCompradores()
  }, [])

  const participacaoCompradoresCompleted =
    !!watch('detalhesDaProposta.participacaoCompradores.titular.id') &&
    !!titularPercentual &&
    !errors?.detalhesDaProposta?.participacaoCompradores

  const resumoDistribuicao = DistribuidorPercentual.obterResumoParticipacao(titularPercentual, compradoresNaoTitular)
  return (
    <CardEtapaItem
      titulo={t('proposta:detalhes_proposta.participacao_comprador.titulo')}
      descricao={t('proposta:detalhes_proposta.participacao_comprador.descricao')}
      completed={participacaoCompradoresCompleted}
    >
      <CardEtapaContent>
        <div className="grid space-y-4">
          <Render.If condition={!!titularPercentual && !!titularId}>
            {() => <CardInfoPercentual resumoDistribuicao={resumoDistribuicao} />}
          </Render.If>
          <div
            className="flex w-full flex-col items-baseline gap-8 border border-neutral-100 p-4 md:flex-row md:flex-wrap"
            data-testid="titular"
          >
            <ComboboxField
              label={t('proposta:detalhes_proposta.participacao_comprador.label.comprador')}
              className="w-full"
              wrapperClassName="w-full md:max-w-50 lg:max-w-96"
              options={cadastroCompradores}
              labelProperty="label"
              valueProperty="id"
              control={control}
              showSearch={false}
              hideClearButton
              name="detalhesDaProposta.participacaoCompradores.titular.id"
              onSelect={() => {
                handleDistribuirParticipacoesQuandoPercentualTitularAlterado()
              }}
            />
            <div className="w-62">
              <NumberInputField
                numberFieldProps={{ maxValue: 100 }}
                label={t('proposta:detalhes_proposta.participacao_comprador.label.participacao')}
                name="detalhesDaProposta.participacaoCompradores.titular.percentualParticipacao"
                control={control}
                onBlur={() => {
                  handleDistribuirParticipacoesQuandoPercentualTitularAlterado()
                }}
              />
            </div>
          </div>
          <Render.If condition={!!(titularPercentual && titularId)}>
            {() =>
              fields.map((field, index) => (
                <div
                  key={field.idField}
                  className="flex flex-col gap-8 border border-neutral-100 p-4 md:flex-row md:flex-wrap"
                  data-testid="comprador"
                >
                  <div className="flex w-full flex-col gap-2 py-[6.5px] md:w-50 lg:min-w-96">
                    <p className="text-sm font-medium">
                      {t('proposta:detalhes_proposta.participacao_comprador.comprador')}
                    </p>
                    <p className="text-sm">{cadastroCompradores?.find((p) => p.id === field.id)!.nome}</p>
                  </div>
                  <div className="w-62">
                    <NumberInputField
                      numberFieldProps={{ maxValue: 100 }}
                      label={t('proposta:detalhes_proposta.participacao_comprador.label.participacao')}
                      name={`detalhesDaProposta.participacaoCompradores.compradores.${index}.percentualParticipacao`}
                      control={control}
                      onBlur={() => {
                        trigger('detalhesDaProposta.participacaoCompradores')
                      }}
                    />
                  </div>
                </div>
              ))
            }
          </Render.If>
        </div>
      </CardEtapaContent>
    </CardEtapaItem>
  )
}
